import {
    OVERCLAIM_ROUTE,
    RH_CONTACT_ROUTE,
    RH_USER_PERMISSION_ROUTE,
    RECORDING_ROUTE,
    RIGHTSHOLDER_SUB_ROUTES,
    BENEFITS_ROUTE,
    ROYALTIES_ROUTE,
} from '@App/navigation/route'
import { ID } from '@App/navigation/index'
import RecordingGroupCategory from '@App/enum/recording-group-category'
import { createRootRightsholderRoute, createWorkNavigation } from '@App/navigation/rightsholder-navigation-creatework'
import {
    catalogNavigation,
    registrationDraftsNavigation,
    registrationHistoryNavigation,
} from '@App/navigation/rightsholder-navigation-catalog'

const createRootOverclaimRoute = (id: ID) => `${createRootRightsholderRoute(id)}/${OVERCLAIM_ROUTE}`
const createOverclaimViewRoute = (id: ID, overclaimHeaderId: number) =>
    `${createRootOverclaimRoute(id)}/${overclaimHeaderId}`
const createOverclaimUploadDocumentsRoute = (id: ID, overclaimHeaderId: number) =>
    `${createOverclaimViewRoute(id, overclaimHeaderId)}/upload-documents`

const rightsholderNavigation = Object.freeze({
    root: (id: ID) => createRootRightsholderRoute(id),
    summary: (id: ID) => `${createRootRightsholderRoute(id)}/${RIGHTSHOLDER_SUB_ROUTES.summary}`,
    benefits: (id: ID) => `${createRootRightsholderRoute(id)}/${BENEFITS_ROUTE}`,
    createWork: {
        root: (id: ID) => createWorkNavigation(id).root,
        wizard: {
            root: (id: ID) => createWorkNavigation(id).wizard.root,
            workDetails: (id: ID) => createWorkNavigation(id).wizard.workDetails,
            writers: (id: ID) => createWorkNavigation(id).wizard.writers,
            publishers: (id: ID) => createWorkNavigation(id).wizard.publishers,
            publishersShares: (id: ID) => createWorkNavigation(id).wizard.publishersShares,
            publishersWriters: (id: ID) => createWorkNavigation(id).wizard.publishersWriters,
            review: (id: ID) => createWorkNavigation(id).wizard.review,
            recordings: (id: ID) => createWorkNavigation(id).wizard.recordings,
            submission: (id: ID) => createWorkNavigation(id).wizard.submission,
        },
        form: {
            root: (id: ID) => createWorkNavigation(id).form.root,
            workDetails: (id: ID) => createWorkNavigation(id).form.workDetails,
            writers: (id: ID) => createWorkNavigation(id).form.writers,
            writersSharesRole: (id: ID) => createWorkNavigation(id).form.writersSharesRole,
            publishers: (id: ID) => createWorkNavigation(id).form.publishers,
            publishersShares: (id: ID) => createWorkNavigation(id).form.publishersShares,
            publishersWriters: (id: ID) => createWorkNavigation(id).form.publishersWriters,
            recordings: (id: ID) => createWorkNavigation(id).form.recordings,
            submission: (id: ID) => createWorkNavigation(id).form.submission,
        },
        bulk: {
            root: (id: ID) => createWorkNavigation(id).bulk.root,
            fileUpload: (id: ID) => createWorkNavigation(id).bulk.fileUpload,
            fileReview: (id: ID) => createWorkNavigation(id).bulk.fileReview,
            submission: (id: ID) => createWorkNavigation(id).bulk.submission,
            edit: {
                root: (id: ID) => createWorkNavigation(id).bulk.edit.root,
                workDetails: (id: ID) => createWorkNavigation(id).bulk.edit.workDetails,
                writers: (id: ID) => createWorkNavigation(id).bulk.edit.writers,
                writersSharesRole: (id: ID) => createWorkNavigation(id).bulk.edit.writersSharesRole,
                publishers: (id: ID) => createWorkNavigation(id).bulk.edit.publishers,
                publishersShares: (id: ID) => createWorkNavigation(id).bulk.edit.publishersShares,
                publishersWriters: (id: ID) => createWorkNavigation(id).bulk.edit.publishersWriters,
                recordings: (id: ID) => createWorkNavigation(id).bulk.edit.recordings,
                submission: (id: ID) => createWorkNavigation(id).bulk.edit.submission,
            },
        },
    },
    catalog: {
        root: (id: ID) => catalogNavigation(id).root,
        work: (id: ID, workId: ID) => catalogNavigation(id).work(workId),
        changeRequests: {
            root: (id: ID) => catalogNavigation(id).changeRequests.root,
            view: (id: ID, suggestionId: ID) => catalogNavigation(id).changeRequests.view(suggestionId),
        },
        workEdit: {
            root: (id: ID, type: string, workId: ID) => catalogNavigation(id).workEdit.root(type, workId),
            workDetails: (id: ID, type: string, workId: ID) => catalogNavigation(id).workEdit.workDetails(type, workId),
            writers: (id: ID, type: string, workId: ID) => catalogNavigation(id).workEdit.writers(type, workId),
            writersSharesRole: (id: ID, type: string, workId: ID) =>
                catalogNavigation(id).workEdit.writersSharesRole(type, workId),
            publishers: (id: ID, type: string, workId: ID) => catalogNavigation(id).workEdit.publishers(type, workId),
            publishersShares: (id: ID, type: string, workId: ID) =>
                catalogNavigation(id).workEdit.publishersShares(type, workId),
            recordings: (id: ID, type: string, workId: ID) => catalogNavigation(id).workEdit.recordings(type, workId),
            submission: (id: ID, type: string, workId: ID) => catalogNavigation(id).workEdit.submission(type, workId),
        },
    },
    overclaim: {
        root: (id: ID) => `${createRootOverclaimRoute(id)}`,
        view: (id: ID, overclaimHeaderId: number) => `${createOverclaimViewRoute(id, overclaimHeaderId)}`,
        uploadDocuments: (id: ID, overclaimHeaderId: number) =>
            `${createOverclaimUploadDocumentsRoute(id, overclaimHeaderId)}`,
    },
    registrationHistory: {
        root: (id: ID) => registrationHistoryNavigation(id).root,
        pending: (id: ID) => registrationHistoryNavigation(id).pending,
        processed: (id: ID) => registrationHistoryNavigation(id).processed,
        needsAttention: (id: ID) => registrationHistoryNavigation(id).needsAttention,
        submission: (id: ID, workId: ID) => registrationHistoryNavigation(id).submission(workId),
        transaction: (id: ID, workId: ID) => registrationHistoryNavigation(id).transaction(workId),
    },
    workMerge: {
        root: (id: ID) => `${createRootRightsholderRoute(id)}/${RIGHTSHOLDER_SUB_ROUTES.workMerge.root}`,
        myMergeRequests: (id: ID) =>
            `${createRootRightsholderRoute(id)}/${RIGHTSHOLDER_SUB_ROUTES.workMerge.root}#my-merge-requests`,
        forMyReview: (id: ID) =>
            `${createRootRightsholderRoute(id)}/${RIGHTSHOLDER_SUB_ROUTES.workMerge.root}#for-my-review`,
        newMergeRequest: (id: ID) =>
            `${createRootRightsholderRoute(id)}/${RIGHTSHOLDER_SUB_ROUTES.workMerge.newRequest}`,
        reviewRequest: (id: ID, workMergeRequestId: ID) =>
            `${createRootRightsholderRoute(id)}/${RIGHTSHOLDER_SUB_ROUTES.workMerge.root}/review/${workMergeRequestId}`,
        request: (id: ID, workMergeRequestId: ID) =>
            `${createRootRightsholderRoute(id)}/${RIGHTSHOLDER_SUB_ROUTES.workMerge.root}/${workMergeRequestId}`,
    },
    registrationDrafts: {
        root: (id: ID) => registrationDraftsNavigation(id).root,
        draft: (id: ID, workId: ID) => registrationDraftsNavigation(id).draft(workId),
        editRoot: (id: ID, type: string, workId: ID) => registrationDraftsNavigation(id).editRoot(type, workId),
        workDetails: (id: ID, type: string, workId: ID) => registrationDraftsNavigation(id).workDetails(type, workId),
        writers: (id: ID, type: string, workId: ID) => registrationDraftsNavigation(id).writers(type, workId),
        writersSharesRole: (id: ID, type: string, workId: ID) =>
            registrationDraftsNavigation(id).writersSharesRole(type, workId),
        publishers: (id: ID, type: string, workId: ID) => registrationDraftsNavigation(id).publishers(type, workId),
        publishersShares: (id: ID, type: string, workId: ID) =>
            registrationDraftsNavigation(id).publishersShares(type, workId),
        recordings: (id: ID, type: string, workId: ID) => registrationDraftsNavigation(id).recordings(type, workId),
        submission: (id: ID, type: string, workId: ID) => registrationDraftsNavigation(id).submission(type, workId),
    },
    matchClaim: {
        match: (id: ID) => `${createRootRightsholderRoute(id)}/${RIGHTSHOLDER_SUB_ROUTES.matchingTool}`,
        claim: (id: ID) => `${createRootRightsholderRoute(id)}/${RIGHTSHOLDER_SUB_ROUTES.claimingTool}`,
        matchHistory: (id: ID) => `${createRootRightsholderRoute(id)}/${RIGHTSHOLDER_SUB_ROUTES.matchHistory.root}`,
        matchUsage: (id: ID, submissionId: ID) =>
            `${createRootRightsholderRoute(id)}/${RIGHTSHOLDER_SUB_ROUTES.matchHistory.root}/${submissionId}`,
        recordingGroups: (id: ID, submissionId: ID, recordingGroupCategory: RecordingGroupCategory) =>
            `${createRootRightsholderRoute(id)}/${
                RIGHTSHOLDER_SUB_ROUTES.matchHistory.root
            }/${submissionId}/${RECORDING_ROUTE}/${recordingGroupCategory}`,
    },
    royalties: {
        root: (id: ID) => `${createRootRightsholderRoute(id)}/${RIGHTSHOLDER_SUB_ROUTES.royalties.root}`,
        statements: (id: ID) =>
            `${createRootRightsholderRoute(id)}/${ROYALTIES_ROUTE}/${RIGHTSHOLDER_SUB_ROUTES.royalties.statements}`,
        statement: (id: ID, payee: number, period: string) =>
            `${createRootRightsholderRoute(id)}/${ROYALTIES_ROUTE}/${
                RIGHTSHOLDER_SUB_ROUTES.royalties.statements
            }?payee=${payee}&period=${period}`,
        rateSheets: (id: ID) =>
            `${createRootRightsholderRoute(id)}/${ROYALTIES_ROUTE}/${RIGHTSHOLDER_SUB_ROUTES.royalties.rateSheets}`,
        annual: (id: ID) =>
            `${createRootRightsholderRoute(id)}/${ROYALTIES_ROUTE}/${RIGHTSHOLDER_SUB_ROUTES.royalties.annualStatements}`,
    },
    maintenance: {
        root: (id: ID) => `${createRootRightsholderRoute(id)}/${RIGHTSHOLDER_SUB_ROUTES.maintenance.root}`,
        userPermissions: (id: ID) =>
            `${createRootRightsholderRoute(id)}/${RIGHTSHOLDER_SUB_ROUTES.maintenance.userPermissions}`,
        userPermissionsProfile: (id: ID, userId: ID) =>
            `${createRootRightsholderRoute(id)}/${RH_USER_PERMISSION_ROUTE}/${userId}`,
        memberContacts: (id: ID) =>
            `${createRootRightsholderRoute(id)}/${RIGHTSHOLDER_SUB_ROUTES.maintenance.rightsholderContacts}`,
        contactInformation: (id: ID, contactId: ID) =>
            `${createRootRightsholderRoute(id)}/${RH_CONTACT_ROUTE}/${contactId}`,
        bankingAndTax: (id: ID) =>
            `${createRootRightsholderRoute(id)}/${RIGHTSHOLDER_SUB_ROUTES.maintenance.root}#banking-and-tax`,
    },
    catalogTransfer: {
        root: (id: ID) => `${createRootRightsholderRoute(id)}/${RIGHTSHOLDER_SUB_ROUTES.catalogTransfer.root}`,
        mySubmissions: (id: ID) =>
            `${createRootRightsholderRoute(id)}/${RIGHTSHOLDER_SUB_ROUTES.catalogTransfer.root}#my-submissions`,
        forMyReview: (id: ID) =>
            `${createRootRightsholderRoute(id)}/${RIGHTSHOLDER_SUB_ROUTES.catalogTransfer.root}#for-my-review`,
        wizardLanding: (id: ID) =>
            `${createRootRightsholderRoute(id)}/${RIGHTSHOLDER_SUB_ROUTES.catalogTransfer.wizardLanding}`,
        wizard: (id: ID, transferId: ID) =>
            `${createRootRightsholderRoute(id)}/${
                RIGHTSHOLDER_SUB_ROUTES.catalogTransfer.wizardLanding
            }/${transferId}/wizard`,
        view: (id: ID, transferId: ID) =>
            `${createRootRightsholderRoute(id)}/${
                RIGHTSHOLDER_SUB_ROUTES.catalogTransfer.wizardLanding
            }/${transferId}/view`,
    },
})

export default rightsholderNavigation
